import { z } from 'zod';

import { SsoProviderSchema, RoleKeySchema } from '../enums.js';

import { UuidSchema } from './common.js';

// auth-service: POST /auth/login, /auth/sso/:provider, /auth/refresh, /auth/logout,
// GET /auth/me, and the internal user lookups.

/** Access-token `iss` and `aud`: auth-service signs with them and the gateway checks them. */
export const ACCESS_TOKEN_ISSUER = 'baseline-auth';
export const ACCESS_TOKEN_AUDIENCE = 'baseline';

/** Messages are the prototype's login validation copy, verbatim. */
export const LoginRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your work email.')
    .pipe(z.email('Enter a valid email, like name@company.com.')),
  password: z.string().min(1, 'Enter your password.'),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const MeSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  initials: z.string().max(3),
  email: z.email(),
  title: z.string().nullable(),
  roles: z.array(RoleKeySchema),
});
export type Me = z.infer<typeof MeSchema>;

/** The refresh token travels only in an httpOnly cookie, never in a body. */
export const TokenResponseSchema = z.object({
  accessToken: z.string().min(1),
  /** Seconds until the access token expires. */
  expiresIn: z.number().int().positive(),
});
export type TokenResponse = z.infer<typeof TokenResponseSchema>;

export const LoginResponseSchema = TokenResponseSchema.extend({ user: MeSchema });
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const SsoParamsSchema = z.object({ provider: SsoProviderSchema });
export type SsoParams = z.infer<typeof SsoParamsSchema>;

// Internal routes

export const UserSummarySchema = MeSchema.extend({ isActive: z.boolean() });
export type UserSummary = z.infer<typeof UserSummarySchema>;

export const UsersResponseSchema = z.object({ users: z.array(UserSummarySchema) });
export type UsersResponse = z.infer<typeof UsersResponseSchema>;

export const UserIdParamsSchema = z.object({ id: UuidSchema });
export const RoleParamsSchema = z.object({ role: RoleKeySchema });
